import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Search, MapPin, Filter, X, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnhancedSearch = ({ onResults, onLoading }) => {
  const [searchParams, setSearchParams] = useState({
    query: '',
    main_category: '',
    subcategory: '',
    location: '',
    discount_type: ''
  });
  
  const [categories, setCategories] = useState({});
  const [discountTypes, setDiscountTypes] = useState({});
  const [availableSubcategories, setAvailableSubcategories] = useState({});
  const [activeFilters, setActiveFilters] = useState([]);
  const [isAdvanced, setIsAdvanced] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data.categories);
        setDiscountTypes(response.data.discount_types);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (searchParams.main_category && categories[searchParams.main_category]) {
      setAvailableSubcategories(categories[searchParams.main_category].subcategories);
    } else {
      setAvailableSubcategories({});
      setSearchParams(prev => ({ ...prev, subcategory: '' }));
    }
  }, [searchParams.main_category, categories]);

  useEffect(() => {
    const filters = [];
    if (searchParams.query) filters.push({ type: 'search', value: searchParams.query, label: `Search: ${searchParams.query}` });
    if (searchParams.main_category) filters.push({ type: 'category', value: searchParams.main_category, label: categories[searchParams.main_category]?.name || searchParams.main_category });
    if (searchParams.subcategory) filters.push({ type: 'subcategory', value: searchParams.subcategory, label: availableSubcategories[searchParams.subcategory] || searchParams.subcategory });
    if (searchParams.location) filters.push({ type: 'location', value: searchParams.location, label: `Location: ${searchParams.location}` });
    if (searchParams.discount_type) filters.push({ type: 'discount', value: searchParams.discount_type, label: discountTypes[searchParams.discount_type] || searchParams.discount_type });
    
    setActiveFilters(filters);
  }, [searchParams, categories, availableSubcategories, discountTypes]);

  const handleSearch = async () => {
    if (onLoading) onLoading(true);
    
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) {
          if (key === 'query') {
            queryParams.append('search', value);
          } else {
            queryParams.append(key, value);
          }
        }
      });
      
      const response = await axios.get(`${API}/businesses?${queryParams.toString()}`);
      if (onResults) onResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
      if (onResults) onResults([]);
    } finally {
      if (onLoading) onLoading(false);
    }
  };

  const clearFilter = (filterType) => {
    setSearchParams(prev => ({ ...prev, [filterType]: '' }));
  };

  const clearAllFilters = () => {
    setSearchParams({
      query: '',
      main_category: '',
      subcategory: '',
      location: '',
      discount_type: ''
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Card className="w-full bg-white/95 backdrop-blur-sm border-orange/20">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-charcoal flex items-center">
            <Search className="h-5 w-5 mr-2 text-primary" />
            Find Live Discounts Near You
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsAdvanced(!isAdvanced)}
            className="text-primary hover:text-primary/80"
          >
            <Filter className="h-4 w-4 mr-1" />
            {isAdvanced ? 'Simple' : 'Advanced'} Search
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Basic Search Bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              placeholder="Search businesses, offers, or products..."
              value={searchParams.query}
              onChange={(e) => setSearchParams(prev => ({ ...prev, query: e.target.value }))}
              onKeyPress={handleKeyPress}
              className="h-12 text-base border-gray-200 focus:border-primary"
            />
          </div>
          <Button 
            onClick={handleSearch}
            size="lg" 
            className="h-12 px-8 bg-primary hover:bg-primary/90"
          >
            <Search className="h-5 w-5 mr-2" />
            Search
          </Button>
        </div>

        {/* Advanced Filters */}
        {isAdvanced && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            {/* Main Category */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Category</label>
              <Select value={searchParams.main_category} onValueChange={(value) => setSearchParams(prev => ({ ...prev, main_category: value }))}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Categories</SelectItem>
                  {Object.entries(categories).map(([key, category]) => (
                    <SelectItem key={key} value={key}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subcategory */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Subcategory</label>
              <Select 
                value={searchParams.subcategory} 
                onValueChange={(value) => setSearchParams(prev => ({ ...prev, subcategory: value }))}
                disabled={!searchParams.main_category}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Types</SelectItem>
                  {Object.entries(availableSubcategories).map(([key, name]) => (
                    <SelectItem key={key} value={key}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Location</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Postcode, city, region..."
                  value={searchParams.location}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, location: e.target.value }))}
                  className="pl-10 h-10 border-gray-200 focus:border-primary"
                />
              </div>
            </div>

            {/* Discount Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Discount Type</label>
              <Select value={searchParams.discount_type} onValueChange={(value) => setSearchParams(prev => ({ ...prev, discount_type: value }))}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Offers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Offers</SelectItem>
                  {Object.entries(discountTypes).map(([key, name]) => (
                    <SelectItem key={key} value={key}>
                      <Tag className="h-3 w-3 mr-1" />
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm font-medium text-gray-600">Active filters:</span>
            {activeFilters.map((filter, index) => (
              <Badge 
                key={index} 
                variant="secondary" 
                className="bg-primary/10 text-primary hover:bg-primary/20 cursor-pointer"
                onClick={() => clearFilter(filter.type === 'search' ? 'query' : filter.type)}
              >
                {filter.label}
                <X className="h-3 w-3 ml-1" />
              </Badge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-gray-500 hover:text-gray-700 h-6 px-2"
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Quick Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm font-medium text-gray-600 self-center">Quick filters:</span>
          {['student_discount', 'nhs_key_worker', '2_for_1', 'flash_sales'].map((discountKey) => (
            <Button
              key={discountKey}
              variant="outline"
              size="sm"
              onClick={() => setSearchParams(prev => ({ ...prev, discount_type: discountKey }))}
              className={`h-8 ${searchParams.discount_type === discountKey ? 'bg-primary text-white border-primary' : 'border-gray-300 hover:border-primary'}`}
            >
              <Tag className="h-3 w-3 mr-1" />
              {discountTypes[discountKey]}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default EnhancedSearch;